/**
 * Medicare charges from CMS (MVP C2 product home — QPP Reports).
 * Authoritative $ + clinician audit list, detail card (confirm/undo org), CSV export.
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { qk } from '@/lib/query-keys';
import {
  useNpiProfiles,
  useResolvedFinancials,
  type NpiProfileRow,
  type ResolvedFinancials,
} from '@/hooks/queries/useMvpQueries';
import { bucketClinicianType, clinicianTypeBucketLabel } from '@/lib/mvp/clinician-type';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import {
  ChevronDown, ChevronRight, Download, DollarSign, HelpCircle, RefreshCw,
} from 'lucide-react';

type FinNpi = ResolvedFinancials['npis'][number];

type CmsOrg = {
  name: string;
  individualEligible: boolean | null;
  groupEligible: boolean | null;
};

type ClinicianRow = FinNpi & {
  origin: 'qpp_file' | 'claims_detected' | null;
  orgNames: string[];
  orgs: CmsOrg[];
  suggestedOrgName: string | null;
  confirmedOrgName: string | null;
  forceInclude: boolean;
  profileMatchTier: string | null;
  countsForPractice: boolean;
};

/** Pending list actions: confirm fuzzy CMS name, or force-include (Add to this practice). */
function pendingPracticeAction(n: ClinicianRow): 'confirm' | 'add' | null {
  if (n.suggestedOrgName && !n.countsForPractice) return 'confirm';
  if (n.origin === 'claims_detected' && !n.countsForPractice) return 'add';
  return null;
}

/** Manual org-link exists — can bulk-remove (not auto QPP-file matches without a link). */
function canRemoveFromPractice(n: ClinicianRow): boolean {
  return !!n.confirmedOrgName;
}

function isSelectableClinician(n: ClinicianRow): boolean {
  return pendingPracticeAction(n) != null || canRemoveFromPractice(n);
}

const fmt = (v: number | null | undefined) =>
  v == null ? '—' : `$${Math.round(v).toLocaleString()}`;

function yn(v: boolean | null | undefined) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

/** Count CMS orgs with a true flag; "—" when eligibility wasn't hydrated yet. */
function eligCount(
  orgs: CmsOrg[],
  key: 'groupEligible' | 'individualEligible',
): number | null {
  if (!orgs.length) return null;
  if (orgs.every(o => o[key] == null)) return null;
  return orgs.filter(o => o[key] === true).length;
}

/** CMS name-match status only (not whether they count for this practice). */
function cmsMatchBadge(n: ClinicianRow) {
  if (n.forceInclude) {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 font-normal">Unconfirmed</Badge>;
  }
  if (n.suggestedOrgName && !n.countsForPractice) {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 font-normal">Suggest</Badge>;
  }
  if (n.origin === 'qpp_file' || n.matchTier === 'exact' || (n.confirmedOrgName && !n.forceInclude)) {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-normal">Matched</Badge>;
  }
  if (n.origin === 'claims_detected') {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 font-normal">Unconfirmed</Badge>;
  }
  return <Badge variant="outline" className="font-normal">—</Badge>;
}

function sourceShort(source: string) {
  if (source.startsWith('excluded')) return 'Excluded';
  if (source.includes('manual')) return 'Override';
  if (source.includes('CMS dataset')) return 'CMS';
  if (source.includes('QPP file')) return 'QPP file';
  return source;
}

function cleanSpecialty(specialty: string | null | undefined) {
  if (!specialty) return null;
  return specialty.replace(/^Physician\//i, '').trim() || specialty;
}

function downloadClinicianCsv(rows: ClinicianRow[], practiceId: string | number, year: number) {
  const headers = [
    'npi', 'clinician_name', 'clinician_type', 'specialty', 'origin',
    'cms_orgs_group_eligible', 'cms_orgs_individual_eligible', 'cms_match',
    'this_practice', 'org_names', 'orgs_group_eligible', 'orgs_individual_eligible',
    'qpp_eligibility_file_charges', 'cms_dataset_charges', 'used_charges', 'source',
  ];
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const thisPracticeLabel = (r: ClinicianRow) => {
    if (r.forceInclude) return 'included';
    if (r.countsForPractice) return 'confirmed';
    if (r.suggestedOrgName) return 'suggest_confirm';
    return 'not_on_practice';
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.npi,
      r.clinicianName,
      r.clinicianType,
      r.specialty,
      r.origin === 'qpp_file' ? 'qpp_eligibility_file' : r.origin,
      eligCount(r.orgs, 'groupEligible') ?? '',
      eligCount(r.orgs, 'individualEligible') ?? '',
      r.forceInclude ? 'unconfirmed' : (r.countsForPractice ? 'matched' : (r.suggestedOrgName ? 'suggest' : 'unconfirmed')),
      thisPracticeLabel(r),
      r.orgNames.join(' | '),
      r.orgs.filter(o => o.groupEligible === true).map(o => o.name).join(' | '),
      r.orgs.filter(o => o.individualEligible === true).map(o => o.name).join(' | '),
      r.fileValue,
      r.datasetValue,
      r.resolved,
      r.source,
    ].map(escape).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `medicare-charges-clinicians-${practiceId}-py${year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function waitForHydrationIdle(practiceId: string | number, year: number, maxMs = 180_000) {
  const started = Date.now();
  await new Promise(r => setTimeout(r, 400));
  while (Date.now() - started < maxMs) {
    const status = await apiClient.get<{ running: boolean }>(
      `/api/mvp/${practiceId}/hydration-status?year=${year}`,
    );
    if (!status.running) return true;
    await new Promise(r => setTimeout(r, 2000));
  }
  return false;
}

export function CmsDataFreshnessCard({ practiceId, programYear }: {
  practiceId: string | number | null; programYear: number;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedNpi, setSelectedNpi] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedNpis, setSelectedNpis] = useState<Set<string>>(() => new Set());

  const financialsQ = useResolvedFinancials(practiceId, programYear);
  const profilesQ = useNpiProfiles(practiceId, programYear);

  const fin = financialsQ.data;
  const lastHydrated = profilesQ.data?.lastHydrated ?? null;

  const profileByNpi = useMemo(() => {
    const m = new Map<string, NpiProfileRow>();
    for (const p of profilesQ.data?.profiles ?? []) m.set(p.npi, p);
    return m;
  }, [profilesQ.data?.profiles]);

  const rows: ClinicianRow[] = useMemo(() => {
    return (fin?.npis ?? []).map(n => {
      const p = profileByNpi.get(n.npi);
      // Prefer resolver type; fall back to hydrated CMS typeDescription
      const clinicianType = n.clinicianType || p?.type_description || null;
      return {
        ...n,
        clinicianType,
        origin: p?.origin ?? (n.inFile ? 'qpp_file' : 'claims_detected'),
        orgNames: p?.org_names ?? [],
        orgs: p?.orgs ?? [],
        suggestedOrgName: p?.suggested_org_name ?? null,
        confirmedOrgName: p?.confirmed_org_name ?? null,
        forceInclude: p?.force_include === true,
        profileMatchTier: p?.match_tier ?? n.matchTier,
        countsForPractice: p?.counts_for_practice ?? (n.orgMatched || n.inFile),
      };
    });
  }, [fin?.npis, profileByNpi]);

  const selected = rows.find(r => r.npi === selectedNpi) ?? null;
  const insights = profilesQ.data?.affiliationInsights;

  const excludedCount = useMemo(
    () => rows.filter(n => n.source === 'excluded (org unconfirmed)').length,
    [rows],
  );

  const selectableRows = useMemo(
    () => rows.filter(isSelectableClinician),
    [rows],
  );

  const pendingRows = useMemo(
    () => rows.filter(n => pendingPracticeAction(n) != null),
    [rows],
  );

  const removableRows = useMemo(
    () => rows.filter(canRemoveFromPractice),
    [rows],
  );

  const allSelectableSelected =
    selectableRows.length > 0 && selectableRows.every(n => selectedNpis.has(n.npi));

  const selectedPendingCount = useMemo(
    () => pendingRows.filter(n => selectedNpis.has(n.npi)).length,
    [pendingRows, selectedNpis],
  );

  const selectedRemovableCount = useMemo(
    () => removableRows.filter(n => selectedNpis.has(n.npi)).length,
    [removableRows, selectedNpis],
  );

  const invalidateMoneyQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['mvp', 'npi-profiles', practiceId, programYear] }),
      queryClient.invalidateQueries({ queryKey: ['mvp', 'financials', practiceId, programYear] }),
      queryClient.invalidateQueries({ queryKey: qk.practiceSettings(practiceId ?? '', programYear) }),
    ]);
  };

  const toggleSelected = (npi: string, on: boolean) => {
    setSelectedNpis(prev => {
      const next = new Set(prev);
      if (on) next.add(npi); else next.delete(npi);
      return next;
    });
  };

  const selectAllSelectable = (on: boolean) => {
    if (!on) {
      setSelectedNpis(new Set());
      return;
    }
    setSelectedNpis(new Set(selectableRows.map(n => n.npi)));
  };

  const confirmSelected = async () => {
    if (!practiceId || selectedPendingCount === 0) return;
    const links: Array<{ npi: string; org_name?: string; force_include?: boolean }> = [];
    for (const n of rows) {
      if (!selectedNpis.has(n.npi)) continue;
      const action = pendingPracticeAction(n);
      if (action === 'confirm' && n.suggestedOrgName) {
        links.push({ npi: n.npi, org_name: n.suggestedOrgName });
      } else if (action === 'add') {
        links.push({ npi: n.npi, force_include: true });
      }
    }
    if (links.length === 0) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/org-links/bulk?year=${programYear}`, { links });
      setSelectedNpis(prev => {
        const next = new Set(prev);
        for (const l of links) next.delete(l.npi);
        return next;
      });
      await invalidateMoneyQueries();
      toast({
        title: links.length === 1 ? 'Clinician added' : `${links.length} clinicians added`,
        description: 'They now count for specialty and Medicare charges for this practice.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to confirm', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = async () => {
    if (!practiceId || selectedRemovableCount === 0) return;
    const npis = removableRows.filter(n => selectedNpis.has(n.npi)).map(n => n.npi);
    if (npis.length === 0) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/org-links/bulk-delete?year=${programYear}`, { npis });
      setSelectedNpis(prev => {
        const next = new Set(prev);
        for (const npi of npis) next.delete(npi);
        return next;
      });
      await invalidateMoneyQueries();
      toast({
        title: npis.length === 1 ? 'Clinician removed' : `${npis.length} clinicians removed`,
        description: 'Excluded from specialty and Medicare charges until matched again.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to remove', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    if (!practiceId) return;
    setRefreshing(true);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/hydrate?year=${programYear}`, {});
      toast({
        title: 'Checking CMS…',
        description: 'Pulling eligibility and Part B charges. Profile Part B updates when this finishes.',
      });
      const finished = await waitForHydrationIdle(practiceId, programYear);
      await invalidateMoneyQueries();
      toast({
        title: finished ? 'CMS refresh complete' : 'Still working…',
        description: finished
          ? 'Medicare charges and Practice Profile Part B are updated.'
          : 'Refresh is taking longer than usual — check again in a minute.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to refresh', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  /** Fuzzy: CMS org name looks like ours — confirm that name IS this practice. */
  const confirmSuggestedOrg = async (npi: string, orgName: string) => {
    if (!practiceId || !orgName) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/org-links/bulk?year=${programYear}`, {
        links: [{ npi, org_name: orgName }],
      });
      setSelectedNpis(prev => {
        const next = new Set(prev);
        next.delete(npi);
        return next;
      });
      await invalidateMoneyQueries();
      toast({
        title: 'Practice name confirmed',
        description: `Treating “${orgName}” as this practice for specialty and Medicare charges.`,
      });
    } catch (e: any) {
      toast({ title: 'Failed to confirm', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  /** CMS does not list this practice — optional override to count the NPI here anyway. */
  const forceIncludeNpi = async (npi: string) => {
    if (!practiceId) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/org-links/bulk?year=${programYear}`, {
        links: [{ npi, force_include: true }],
      });
      setSelectedNpis(prev => {
        const next = new Set(prev);
        next.delete(npi);
        return next;
      });
      await invalidateMoneyQueries();
      toast({
        title: 'Clinician included',
        description: 'Counted for this practice even though CMS does not list your organization for this NPI.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to include', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const undoConfirm = async (npi: string) => {
    if (!practiceId) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/mvp/${practiceId}/org-links/${npi}?year=${programYear}`);
      setSelectedNpis(prev => {
        const next = new Set(prev);
        next.delete(npi);
        return next;
      });
      await invalidateMoneyQueries();
      toast({
        title: 'Removed',
        description: 'Clinician excluded from specialty and revenue until matched again.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to undo', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (!practiceId) return null;

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="w-5 h-5" />
                Medicare charges (from CMS)
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="How this is calculated">
                        <HelpCircle className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs leading-relaxed">
                      Your QPP eligibility file is never overwritten. We pull CMS eligibility and Part B
                      allowed charges, apply the org-count rule so multi-TIN clinicians don’t inflate this
                      practice, and use that figure for dollars and Practice Profile Part B.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </div>
            <Button size="sm" variant="outline" disabled={refreshing} onClick={refresh}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-3xl font-semibold tabular-nums">{fmt(fin?.tin.resolved)}</div>
          <div className="text-sm text-muted-foreground">
            {fin ? <>Source: {fin.tin.source}</> : 'Loading…'}
            {lastHydrated
              ? <> · Last checked CMS {new Date(lastHydrated).toLocaleDateString()}</>
              : ' · Not yet checked against CMS APIs'}
          </div>
          {(insights || excludedCount > 0) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {insights && (
                <>
                  <span>
                    <span className="font-medium text-foreground tabular-nums">{insights.groupEligible}</span>
                    {' '}affiliations group-eligible
                  </span>
                  <span>
                    <span className="font-medium text-foreground tabular-nums">{insights.individualEligible}</span>
                    {' '}affiliations individually eligible
                  </span>
                </>
              )}
              {excludedCount > 0 && (
                <span className="text-amber-700">
                  {excludedCount} not counted for this practice
                </span>
              )}
            </div>
          )}

          {rows.length > 0 && (
            <Collapsible open={detailOpen} onOpenChange={setDetailOpen}>
              <div className="flex items-center justify-between gap-2 pt-1">
                <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary">
                  {detailOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Clinician detail ({rows.length})
                </CollapsibleTrigger>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => downloadClinicianCsv(rows, practiceId, programYear)}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download CSV
                </Button>
              </div>
              <CollapsibleContent>
                <TooltipProvider delayDuration={200}>
                  {selectableRows.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-slate-50/80 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <Label className="flex items-center gap-2 font-normal cursor-pointer">
                          <Checkbox
                            checked={allSelectableSelected}
                            onCheckedChange={v => selectAllSelectable(v === true)}
                            disabled={busy}
                          />
                          Select all ({selectableRows.length})
                        </Label>
                        {pendingRows.length > 0 && (
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline disabled:opacity-50"
                            disabled={busy}
                            onClick={() => setSelectedNpis(new Set(pendingRows.map(n => n.npi)))}
                          >
                            Pending only ({pendingRows.length})
                          </button>
                        )}
                        {removableRows.length > 0 && (
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline disabled:opacity-50"
                            disabled={busy}
                            onClick={() => setSelectedNpis(new Set(removableRows.map(n => n.npi)))}
                          >
                            Confirmed only ({removableRows.length})
                          </button>
                        )}
                        {selectedNpis.size > 0 && (
                          <span className="text-muted-foreground tabular-nums">
                            {selectedNpis.size} selected
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedNpis.size > 0 && (
                          <Button size="sm" variant="ghost" className="h-8" disabled={busy}
                            onClick={() => setSelectedNpis(new Set())}>
                            Clear
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-red-700 border-red-200 hover:bg-red-50"
                          disabled={busy || selectedRemovableCount === 0}
                          onClick={removeSelected}
                        >
                          {busy ? 'Saving…' : `Remove (${selectedRemovableCount})`}
                        </Button>
                        <Button
                          size="sm"
                          className="h-8"
                          disabled={busy || selectedPendingCount === 0}
                          onClick={confirmSelected}
                        >
                          {busy ? 'Saving…' : `Confirm (${selectedPendingCount})`}
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 overflow-hidden rounded-lg border">
                    <div className="hidden xl:grid grid-cols-[2rem_minmax(0,1.5fr)_5.5rem_minmax(0,1fr)_3.5rem_3.5rem_6.5rem_minmax(0,1fr)_5.5rem] gap-3 px-4 py-2.5 text-xs font-medium text-muted-foreground bg-slate-50 border-b">
                      <div aria-hidden />
                      <div>Clinician</div>
                      <div>Type</div>
                      <div>Specialty</div>
                      <div className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">Group</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            How many CMS-associated practices this NPI is group-eligible under (MIPS group).
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">Indiv</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            How many CMS-associated practices this NPI is individually MIPS-eligible under.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">CMS match</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            Whether CMS’s organization name for this NPI matches this practice (auto or suggested).
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">This practice</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            Select then Confirm (add) or Remove (undo manual link). Suggest = CMS name looks
                            like yours; Not on practice = override when CMS lists other TINs only.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-right">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/50">Used</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            Amount counted in this practice’s Medicare charges after org-match rules.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="max-h-[28rem] overflow-y-auto divide-y bg-white">
                      {rows.map(n => {
                        const bucket = bucketClinicianType(n.clinicianType);
                        const excluded = n.source.startsWith('excluded');
                        const pending = pendingPracticeAction(n);
                        const removable = canRemoveFromPractice(n);
                        const selectable = pending != null || removable;
                        const groupEligN = eligCount(n.orgs, 'groupEligible');
                        const indivEligN = eligCount(n.orgs, 'individualEligible');
                        const isChecked = selectedNpis.has(n.npi);
                        return (
                          <div
                            key={n.npi}
                            className={`px-4 py-3 hover:bg-slate-50/80 transition-colors ${
                              excluded ? 'bg-amber-50/30' : ''
                            } ${isChecked ? 'bg-primary/5' : ''}`}
                          >
                            <div className="grid grid-cols-1 xl:grid-cols-[2rem_minmax(0,1.5fr)_5.5rem_minmax(0,1fr)_3.5rem_3.5rem_6.5rem_minmax(0,1fr)_5.5rem] gap-2 xl:gap-3 xl:items-center">
                              <div className="flex items-center xl:justify-center">
                                {selectable ? (
                                  <Checkbox
                                    checked={isChecked}
                                    disabled={busy}
                                    onCheckedChange={v => toggleSelected(n.npi, v === true)}
                                    aria-label={`Select ${n.clinicianName || n.npi}`}
                                  />
                                ) : (
                                  <span className="w-4" />
                                )}
                              </div>

                              <button
                                type="button"
                                className="min-w-0 text-left rounded-md -mx-1 px-1 py-0.5 hover:bg-slate-100/80"
                                onClick={() => setSelectedNpi(n.npi)}
                              >
                                <div className="font-medium text-sm truncate">
                                  {n.clinicianName || 'Name unavailable'}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                  <span className="font-mono">{n.npi}</span>
                                  <span>·</span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                                        {n.origin === 'qpp_file' ? 'File' : 'Claims'}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs text-xs">
                                      {n.origin === 'qpp_file'
                                        ? 'On this practice’s QPP eligibility file (CMS’s official roster for the TIN).'
                                        : 'Seen in uploaded claims for this practice, but not on this practice’s QPP eligibility file.'}
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </button>

                              <div className="text-sm min-w-0">
                                {bucket ? (
                                  <Badge variant="secondary" className="font-normal" title={n.clinicianType ?? undefined}>
                                    {clinicianTypeBucketLabel(bucket)}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground text-xs truncate block">
                                    {n.clinicianType || '—'}
                                  </span>
                                )}
                              </div>

                              <div className="text-sm text-muted-foreground truncate min-w-0">
                                {cleanSpecialty(n.specialty) || '—'}
                              </div>

                              <div className="text-sm tabular-nums text-center xl:text-center">
                                <span className="xl:hidden text-xs text-muted-foreground mr-1">Group</span>
                                {groupEligN == null ? '—' : groupEligN}
                              </div>
                              <div className="text-sm tabular-nums text-center xl:text-center">
                                <span className="xl:hidden text-xs text-muted-foreground mr-1">Indiv</span>
                                {indivEligN == null ? '—' : indivEligN}
                              </div>

                              <div>{cmsMatchBadge(n)}</div>

                              <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                {n.countsForPractice && !n.forceInclude && (
                                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-normal">
                                    Confirmed
                                  </Badge>
                                )}
                                {n.forceInclude && (
                                  <Badge variant="outline" className="border-amber-400 text-amber-800 font-normal">
                                    Included
                                  </Badge>
                                )}
                                {pending === 'confirm' && (
                                  <Badge variant="outline" className="border-amber-400 text-amber-800 font-normal">
                                    Suggest
                                  </Badge>
                                )}
                                {pending === 'add' && (
                                  <Badge variant="outline" className="font-normal">
                                    Not on practice
                                  </Badge>
                                )}
                              </div>

                              <div className="xl:text-right">
                                <div className="text-sm font-semibold tabular-nums">{fmt(n.resolved)}</div>
                                <div className="text-[11px] text-muted-foreground">{sourceShort(n.source)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TooltipProvider>
                <p className="text-xs text-muted-foreground mt-2">
                  Select clinicians, then Confirm to add or Remove to undo. Auto-matched QPP file roster
                  (no manual link) cannot be removed here. Click a name for CMS org detail.
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelectedNpi(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">
                  {selected.clinicianName || 'Clinician'}
                </DialogTitle>
                <DialogDescription className="font-mono text-xs">
                  NPI {selected.npi}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border bg-slate-50/80 p-3 space-y-1">
                    <div className="text-xs text-muted-foreground">Type</div>
                    <div className="font-medium">{selected.clinicianType || '—'}</div>
                    {bucketClinicianType(selected.clinicianType) && (
                      <Badge variant="secondary" className="text-[10px] font-normal mt-1">
                        {clinicianTypeBucketLabel(bucketClinicianType(selected.clinicianType)!)}
                      </Badge>
                    )}
                  </div>
                  <div className="rounded-lg border bg-slate-50/80 p-3 space-y-1">
                    <div className="text-xs text-muted-foreground">Specialty (CMS)</div>
                    <div className="font-medium">{cleanSpecialty(selected.specialty) || '—'}</div>
                  </div>
                  <div className="rounded-lg border bg-slate-50/80 p-3 space-y-1">
                    <div className="text-xs text-muted-foreground">Roster origin</div>
                    <div className="font-medium">
                      {selected.origin === 'qpp_file' ? 'QPP eligibility file' : 'Claims'}
                    </div>
                  </div>
                  <div className="rounded-lg border bg-slate-50/80 p-3 space-y-1">
                    <div className="text-xs text-muted-foreground">CMS match</div>
                    <div className="pt-0.5">{cmsMatchBadge(selected)}</div>
                  </div>
                </div>

                <div className="rounded-lg border p-3 space-y-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Medicare charges
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-xs text-muted-foreground">QPP eligibility file</div>
                      <div className="font-semibold tabular-nums">{fmt(selected.fileValue)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">CMS dataset</div>
                      <div className="font-semibold tabular-nums">{fmt(selected.datasetValue)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Used</div>
                      <div className="font-semibold tabular-nums">{fmt(selected.resolved)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground pt-1">{selected.source}</div>
                </div>

                <div className="rounded-lg border p-3 space-y-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    CMS associated practices
                  </div>
                  {selected.orgs.length === 0 && selected.orgNames.length === 0 ? (
                    <p className="text-muted-foreground text-xs">
                      No organization names from CMS yet — run Refresh from CMS.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {(selected.orgs.length > 0
                        ? selected.orgs
                        : selected.orgNames.map(name => ({
                          name,
                          individualEligible: null as boolean | null,
                          groupEligible: null as boolean | null,
                        }))
                      ).map(org => (
                        <li key={org.name} className="rounded-md border bg-slate-50/60 px-3 py-2">
                          <div className="text-sm font-medium">
                            {org.name}
                            {selected.confirmedOrgName === org.name && (
                              <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-normal text-[10px]">
                                Your confirm
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span>Individual: <span className="font-medium text-foreground">{yn(org.individualEligible)}</span></span>
                            <span>Group: <span className="font-medium text-foreground">{yn(org.groupEligible)}</span></span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {selected.confirmedOrgName ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                    <div className="text-sm min-w-0">
                      <div className="font-medium">
                        {selected.forceInclude ? 'Included for this practice' : 'Confirmed for this practice'}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{selected.confirmedOrgName}</div>
                    </div>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => undoConfirm(selected.npi)}>
                      Undo
                    </Button>
                  </div>
                ) : selected.suggestedOrgName && !selected.countsForPractice ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
                    <div className="text-sm font-medium">CMS name looks like this practice</div>
                    <p className="text-xs text-muted-foreground">
                      Confirm that <strong>{selected.suggestedOrgName}</strong> is this practice so the
                      clinician counts for specialty and Medicare charges.
                    </p>
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => confirmSuggestedOrg(selected.npi, selected.suggestedOrgName!)}
                    >
                      Confirm name is ours
                    </Button>
                  </div>
                ) : selected.origin === 'claims_detected' && !selected.countsForPractice ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
                    <div className="text-sm font-medium">Not listed under this practice at CMS</div>
                    <p className="text-xs text-muted-foreground">
                      CMS associates this NPI with the organizations above — none match this practice.
                      Add only if you know they belong here; their CMS charges will count in your total.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-900"
                      disabled={busy}
                      onClick={() => forceIncludeNpi(selected.npi)}
                    >
                      Add to this practice
                    </Button>
                  </div>
                ) : selected.countsForPractice ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-sm">
                    <span className="font-medium">Confirmed</span>
                    <span className="text-muted-foreground"> — counts for this practice’s specialty and Medicare charges.</span>
                  </div>
                ) : null}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedNpi(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
